/**
 * Feature maturity stage badge - shared so the stage pill reads the same on every surface
 * (composer pickers, slash-command menu, deal-page tabs, and any future control). Gating of
 * WHICH stages show happens at the call site via `isStageAvailable(stage, workspace.featureMinStage)`
 * (`@protoss/shared`); this module only renders the badge for a stage the caller has decided to show.
 */

import type { FeatureStage } from '@protoss/shared';
import { Pill, type PillVariant } from '@protoss/ui';

/**
 * Pill colour per maturity stage. alpha = warning (amber, most experimental); beta = accent
 * (closer to GA). GA has no badge (ga/absent stage renders nothing).
 */
export const STAGE_PILL_VARIANT: Record<'alpha' | 'beta', PillVariant> = {
  alpha: 'warning',
  beta: 'accent',
};

/**
 * Renders the maturity-stage pill, or nothing when the stage is absent or 'ga'. Accepts the full
 * FeatureStage so callers can pass a server value (e.g. a menu item's stage) without narrowing.
 */
export function StageBadge({
  stage,
  className,
}: {
  stage?: FeatureStage | null;
  className?: string;
}) {
  if (!stage || stage === 'ga') return null;
  return (
    <Pill variant={STAGE_PILL_VARIANT[stage]} className={className}>
      {stage}
    </Pill>
  );
}
